import { useShallow } from 'zustand/react/shallow';
import useHandleApiError from '../../../hooks/useHandleApiError';
import useOrderBookStore from '../../../stores/OrderBook/orderBook.store';
import useOrderBook from '../../order-book/hooks/useOrderBook';
import { useTranslation } from '../../translation/TranslationContext';
import { ProductSearchResultItem, ProductSearchResultItemColor } from '../globalSearch.types';
import useProductDetailsLauncher from './useProductDetailsLauncher';

const useAddToOrder = () => {
  const { translate } = useTranslation();
  const { getApiErrorMessage } = useHandleApiError();
  const { openOrderBook } = useOrderBook();
  const { openProductDetails } = useProductDetailsLauncher();
  const [selectedOrder, orderProductsIds, isShopping, toggleShopping, toggleShoppingKit] =
    useOrderBookStore(
      useShallow((state) => [
        state.selectedOrder,
        state.orderProductsIds,
        state.isShopping,
        state.toggleShopping,
        state.toggleShoppingKit,
      ]),
    );

  const isProductInActiveOrder = (item: ProductSearchResultItem) =>
    Boolean(selectedOrder) &&
    (item.kitId
      ? isShopping(item.kitId, orderProductsIds)
      : item.colors.some((color) => isShopping(color.id, orderProductsIds)));

  const openProductForView = (item: ProductSearchResultItem) => {
    if (item.kitId) {
      openProductDetails({ kitId: item.kitId });
      return;
    }

    openProductDetails({ productId: item.id, productColorId: item.colors[0]?.id });
  };

  const resolveToggleableColorId = (colors: ProductSearchResultItemColor[]) => {
    const colorInOrder = colors.find((color) => isShopping(color.id, orderProductsIds));
    return (colorInOrder ?? colors[0])?.id;
  };

  const addProductToOrder = (item: ProductSearchResultItem) => {
    if (!selectedOrder) {
      openOrderBook();
      return;
    }

    if (item.kitId) {
      toggleShoppingKit({ kitId: item.kitId, translate, getApiErrorMessage });
      return;
    }

    const productColorId = resolveToggleableColorId(item.colors);

    if (!productColorId) {
      return;
    }

    toggleShopping({ productColorId, translate });
  };

  return {
    hasSelectedOrder: Boolean(selectedOrder),
    isProductInActiveOrder,
    openProductForView,
    addProductToOrder,
  };
};

export default useAddToOrder;
